if (!customElements.get('collection-tabs-block')) {
  customElements.define(
    'collection-tabs-block',
    class CollectionTabsBlock extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.scrollHandler = this.updateScrollControls.bind(this);
        this.resizeHandler = this.updateScrollControls.bind(this);

        this.blockId = this.dataset.blockId;
        this.activePanel = this.querySelector('[data-active-panel]');
        this.tabButtons = Array.from(this.querySelectorAll('[data-tab-target]'));
        const reconnecting = Boolean(this.templates);
        this.templates ||= new Map();
        this.querySelectorAll('template[data-tab-panel]').forEach((template) => {
          this.templates.set(template.dataset.tabPanel, template.content);
        });
        if (!this.activePanel) return;
        this.activeTab = this.activePanel.dataset.panelId;
        if (!this.templates.has(this.activeTab)) {
          this.templates.set(this.activeTab, this.activePanel.cloneNode(true));
        }
        if (reconnecting) this.activeTab = null;
        this.tabScrollShell = this.querySelector('[data-scroll-shell="tabs"]');
        this.productScrollShell = this.querySelector('[data-scroll-shell="products"]');
        this.tabScrollArea = this.querySelector('[data-scroll-area="tabs"]');
        this.productScrollArea = null;

        this.bindEvents();
        this.bindScrollArea('tabs', this.tabScrollArea);

        const initialTab =
          this.tabButtons.find((button) => button.classList.contains('is-active'))?.dataset.tabTarget ||
          this.tabButtons[0]?.dataset.tabTarget ||
          'all';
        this.bindScrollArea('products', this.activePanel.querySelector('[data-scroll-area="products"]'));
        if (initialTab !== this.activeTab) this.activateTab(initialTab);
        window.requestAnimationFrame(() => {
          this.captureOwnedModals();
          this.updateScrollControls();
        });
      }

      disconnectedCallback() {
        this.cleanupOwnedModals();
        this.bindScrollArea('tabs', null);
        this.bindScrollArea('products', null);
        this.abortController?.abort();
      }

      bindEvents() {
        const { signal } = this.abortController;

        this.tabButtons.forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              this.activateTab(button.dataset.tabTarget);
            },
            { signal }
          );
        });

        this.querySelector('.collection-tabs-block__tab-list')?.addEventListener(
          'keydown',
          (event) => {
            this.handleTabKeydown(event);
          },
          { signal }
        );

        this.querySelectorAll('[data-scroll-control]').forEach((button) => {
          button.addEventListener(
            'click',
            () => {
              const direction = button.dataset.direction === 'prev' ? -1 : 1;
              this.scrollByStep(button.dataset.scrollControl, direction);
            },
            { signal }
          );
        });

        window.addEventListener('resize', this.resizeHandler, { signal });
      }

      handleTabKeydown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) || this.tabButtons.length < 2) {
          return;
        }

        event.preventDefault();

        const currentIndex = this.tabButtons.findIndex((button) => button.classList.contains('is-active'));
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') {
          nextIndex = currentIndex === this.tabButtons.length - 1 ? 0 : currentIndex + 1;
        } else if (event.key === 'ArrowLeft') {
          nextIndex = currentIndex <= 0 ? this.tabButtons.length - 1 : currentIndex - 1;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = this.tabButtons.length - 1;
        }

        const nextButton = this.tabButtons[nextIndex];

        if (nextButton) {
          nextButton.focus();
          this.activateTab(nextButton.dataset.tabTarget);
        }
      }

      activateTab(tabId) {
        const template = this.templates.get(tabId);

        if (!template || !this.activePanel || tabId === this.activeTab) {
          return;
        }

        this.tabButtons.forEach((button) => {
          const isActive = button.dataset.tabTarget === tabId;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.cleanupOwnedModals();
        const content = template.cloneNode(true);
        this.activePanel.replaceChildren(...content.childNodes);
        this.activeTab = tabId;
        this.activePanel.dataset.panelId = tabId;

        this.bindScrollArea('products', this.activePanel.querySelector('[data-scroll-area="products"]'));

        window.requestAnimationFrame(() => {
          this.captureOwnedModals();
          this.centerActiveTab();
          this.updateScrollControls();
        });
      }

      bindScrollArea(type, element) {
        const property = type === 'tabs' ? 'tabScrollArea' : 'productScrollArea';
        const currentElement = this[property];

        if (currentElement && currentElement !== element) {
          currentElement.removeEventListener('scroll', this.scrollHandler);
        }

        this[property] = element;

        if (element) {
          element.addEventListener('scroll', this.scrollHandler, { passive: true });
        }
      }

      scrollByStep(target, direction) {
        const element = target === 'tabs' ? this.tabScrollArea : this.productScrollArea;

        if (!element) {
          return;
        }

        const minimumStep = target === 'tabs' ? 180 : 260;
        const step = Math.max(element.clientWidth * 0.8, minimumStep);

        element.scrollBy({
          left: step * direction,
          behavior: 'smooth',
        });
      }

      centerActiveTab() {
        const activeButton = this.tabButtons.find((button) => button.classList.contains('is-active'));

        if (!this.tabScrollArea || !activeButton) {
          return;
        }

        const targetLeft = activeButton.offsetLeft - (this.tabScrollArea.clientWidth - activeButton.offsetWidth) / 2;

        this.tabScrollArea.scrollTo({
          left: Math.max(targetLeft, 0),
          behavior: 'smooth',
        });
      }

      updateScrollControls() {
        this.updateScrollShell(this.tabScrollShell, this.tabScrollArea);
        this.updateScrollShell(this.productScrollShell, this.productScrollArea);
      }

      updateScrollShell(shell, element) {
        if (!shell) {
          return;
        }

        const previousButton = shell.querySelector('[data-direction="prev"]');
        const nextButton = shell.querySelector('[data-direction="next"]');

        if (!element) {
          shell.dataset.canScroll = 'false';

          if (previousButton) previousButton.disabled = true;
          if (nextButton) nextButton.disabled = true;

          return;
        }

        const maxScrollLeft = Math.max(element.scrollWidth - element.clientWidth, 0);
        const canScroll = maxScrollLeft > 8;
        const isAtStart = element.scrollLeft <= 8;
        const isAtEnd = element.scrollLeft >= maxScrollLeft - 8;

        shell.dataset.canScroll = canScroll ? 'true' : 'false';

        if (previousButton) previousButton.disabled = !canScroll || isAtStart;
        if (nextButton) nextButton.disabled = !canScroll || isAtEnd;
      }

      captureOwnedModals() {
        if (!this.activePanel) {
          return;
        }

        this.activePanel.querySelectorAll('modal-opener[data-modal]').forEach((opener) => {
          const selector = opener.getAttribute('data-modal');

          if (!selector) {
            return;
          }

          const modal = document.querySelector(selector);

          if (modal) {
            modal.dataset.collectionTabsOwner = this.blockId;
          }
        });
      }

      cleanupOwnedModals() {
        if (!this.blockId) {
          return;
        }

        document
          .querySelectorAll(`quick-add-modal[data-collection-tabs-owner="${this.blockId}"]`)
          .forEach((modal) => {
            if (typeof modal.hide === 'function' && modal.hasAttribute('open')) {
              modal.hide(true);
            }

            modal.remove();
          });
      }
    }
  );
}
